/**
 * GenericSelectorDropdown - Flexible dropdown component
 *
 * A dropdown component that handles two display modes:
 *   1. Card mode (with renderTitle): Shows title when closed, search when open, card styling
 *   2. Standard mode (without renderTitle): Traditional dropdown with inline selected value
 * - Custom option and value rendering
 * - Controlled menu state
 * - Optional icons in dropdown options (icons are displayed in options but not in selected values)
 */

import {Icon, type IconName} from '@wandb/components';
import {Select, type SelectSize} from '@wandb/weave/components/Form/Select';
import React, {
  forwardRef,
  type JSX,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type ClearIndicatorProps,
  components,
  type ContainerProps,
  type ControlProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type IndicatorsContainerProps,
  type InputActionMeta,
  type MenuListProps,
  type MenuProps,
  type MultiValue,
  type MultiValueProps,
  type OnChangeValue,
  type OptionProps,
  type PropsValue,
  type SelectInstance,
  type SingleValue,
  type SingleValueProps,
  type ValueContainerProps,
} from 'react-select';
import {useClickAway} from 'react-use';
import {twMerge} from 'tailwind-merge';

import {
  ABSOLUTE_OVERLAY,
  CARD_BG_CLOSED,
  CARD_BG_OPEN,
  CARD_PADDING,
  FIXED_SIZE,
  FLEX_COL,
  FLEX_CONTAINER,
  FLEX_ROW_CENTER,
} from './cardStyles';

// Match GenericSliderDropdown shadow: 0px 12px 24px 0px rgba(13,15,18,0.16).
// We apply one shadow per edge (top/left/right or bottom/left/right), so use ~1/3 opacity
// per side so the combined result isn't 3x heavier than the slider's single shadow.
// Negative spread (-8px) keeps each shadow from bleeding past the seam where control meets menu.
const SHADOW_COLOR = 'rgba(13, 15, 18, 0.06)';
const SHADOW_SPREAD = '-8px';
const DROP_SHADOW_TOP = `0px -12px 24px ${SHADOW_SPREAD} ${SHADOW_COLOR}`;
const DROP_SHADOW_BOTTOM = `0px 12px 24px ${SHADOW_SPREAD} ${SHADOW_COLOR}`;
const DROP_SHADOW_LEFT = `-12px 0 24px ${SHADOW_SPREAD} ${SHADOW_COLOR}`;
const DROP_SHADOW_RIGHT = `12px 0 24px ${SHADOW_SPREAD} ${SHADOW_COLOR}`;

function getOpenControlBoxShadow(menuPlacement: 'top' | 'bottom'): string {
  // No shadow on the side that meets the menu (control meets menu on its bottom when menu is below, on its top when menu is above)
  return menuPlacement === 'top'
    ? [DROP_SHADOW_BOTTOM, DROP_SHADOW_LEFT, DROP_SHADOW_RIGHT].join(', ')
    : [DROP_SHADOW_TOP, DROP_SHADOW_LEFT, DROP_SHADOW_RIGHT].join(', ');
}

function getOpenMenuBoxShadow(placement: 'top' | 'bottom'): string {
  // No shadow on the side that meets the control (menu meets control on its bottom when menu is below, on its top when menu is above)
  return placement === 'top'
    ? [DROP_SHADOW_TOP, DROP_SHADOW_LEFT, DROP_SHADOW_RIGHT].join(', ')
    : [DROP_SHADOW_BOTTOM, DROP_SHADOW_LEFT, DROP_SHADOW_RIGHT].join(', ');
}

export type SelectorOption<T = any> = {
  value: string;
  label: string;
  /** Optional data payload - if not provided, value is used */
  data?: T;
  icon?: IconName; // Optional icon name displayed in dropdown options only (not in selected value)
};

type GenericSelectorDropdownProps<T, IsMulti extends boolean = false> = {
  /** Size of the dropdown */
  size: SelectSize;
  /** Currently selected value(s) */
  selectedValue: IsMulti extends true
    ? readonly SelectorOption<T>[]
    : SelectorOption<T> | null;
  /** Callback when selection changes (single mode) */
  onSelect?: (data: T | null) => void;
  /** Callback when selection changes (multi mode) */
  onSelectMulti?: (data: T[]) => void;
  /** All available options */
  options: SelectorOption<T>[];
  /** Whether this is multi-select */
  isMulti: IsMulti;
  /** Whether the dropdown is searchable */
  isSearchable?: boolean;
  /** Whether the dropdown shows a clear indicator */
  isClearable?: boolean;
  /** Control whether menu is open (for controlled usage) */
  menuIsOpen?: boolean;
  /** Callback when menu should open */
  onMenuOpen?: () => void;
  /** Callback when menu should close */
  onMenuClose?: () => void;
  /** Custom placeholder text */
  placeholder?: string;
  /** Custom option renderer */
  renderOption?: (
    data: T,
    props: OptionProps<SelectorOption<T>, IsMulti>
  ) => ReactNode;
  /** Custom title renderer for card mode - when provided, enables card behavior */
  renderTitle?: (data: T | null) => ReactNode;
  /** Custom value renderer for standard mode - shows selected value inline */
  renderValue?: (
    data: T,
    props: ValueContainerProps<SelectorOption<T>, IsMulti>
  ) => ReactNode;
  /** Optional menu item classes for styling */
  menuItemClasses?: string;
  /** Optional value classes for styling (standard mode) */
  valueClasses?: string;
  /** Additional className for the container */
  className?: string;
  /** Server-side search: called when the user types in the search input. */
  onInputChange?: (value: string) => void;
  /** When null, disables client-side filtering (server returns filtered results). */
  filterOption?: null;
  /** Shows a loading indicator while server results are fetching. */
  isLoading?: boolean;
  /** Marks specific options as non-selectable (e.g. truncation hints). */
  isOptionDisabled?: (option: SelectorOption<T>) => boolean;
};

// ============================================================================
// Static Component Factories
// ============================================================================

const createCustomOption = <T, IsMulti extends boolean>(
  renderOption?: (
    data: T,
    props: OptionProps<SelectorOption<T>, IsMulti>
  ) => ReactNode,
  menuItemClasses?: string
) => {
  const CustomOption = (
    props: OptionProps<SelectorOption<T>, IsMulti, GroupBase<SelectorOption<T>>>
  ) => {
    // Use value as fallback if data is not provided
    const data = (props.data.data ?? props.data.value) as T;
    const {icon} = props.data;
    const isSelected = props.isSelected;

    return (
      <components.Option
        {...props}
        className="flex-1 mx-0 py-8 px-16 bg-transparent hover:bg-[light-dark(var(--moon-100),var(--moon-850))] transition-colors">
        <div className="flex flex-row items-start justify-between w-full gap-8">
          <div className="flex flex-1 flex-row items-start gap-8 min-w-0">
            {icon && (
              <div
                className="flex flex-shrink-0 items-center justify-center py-4 text-wb-text-tertiary"
                style={{lineHeight: 0}}>
                <Icon icon={icon as IconName} size="sm" />
              </div>
            )}
            <div className="flex-1 min-w-0 leading-6">
              {renderOption ? (
                renderOption(data, props)
              ) : (
                <span className={twMerge(menuItemClasses, 'break-words')}>
                  {props.data.label}
                </span>
              )}
            </div>
          </div>
          {isSelected && (
            <div
              className="flex flex-shrink-0 items-center justify-center py-4"
              style={{lineHeight: 0}}>
              <Icon icon="checkmark2" size="sm" color="moon" />
            </div>
          )}
        </div>
      </components.Option>
    );
  };
  return CustomOption;
};

// Don't show the icon outside the menu.
const createSingleValue = <T, IsMulti extends boolean>() => {
  const CustomSingleValue = (
    props: SingleValueProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    return (
      <components.SingleValue {...props}>
        <span className="truncate">{props.data.label}</span>
      </components.SingleValue>
    );
  };
  return CustomSingleValue;
};

// Deprecated: not currently supporting multi-select
const createMultiValue = <T, IsMulti extends boolean>() => {
  const CustomMultiValue = (
    props: MultiValueProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    return (
      <components.MultiValue {...props}>
        <components.MultiValueLabel {...props}>
          <span className="truncate">{props.data.label}</span>
        </components.MultiValueLabel>
      </components.MultiValue>
    );
  };
  return CustomMultiValue;
};

// Module-level custom Input for card mode.
// react-select's default Input uses an auto-sizing technique that starts at ~2px wide
// when the value is empty. This makes the input effectively un-clickable when the
// Placeholder overlays it. By replacing it with a flex-1 native <input>, the element
// fills all available space in the ValueContainer so any click in the search area
// directly hits the focusable input element.
// All fields optional so this type is structurally a supertype of any react-select
// InputProps instantiation — making CardSearchInput assignable as a ComponentType.
type CardSearchInputProps = {
  innerRef?: React.Ref<HTMLInputElement>;
  value?: React.InputHTMLAttributes<HTMLInputElement>['value'];
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  selectProps?: {placeholder?: React.ReactNode};
};

const CardSearchInput = ({
  innerRef,
  value,
  onChange,
  onBlur,
  onFocus,
  selectProps,
}: CardSearchInputProps) => {
  return (
    <input
      ref={innerRef}
      className="flex-1 outline-none bg-transparent text-sm text-wb-text-primary placeholder:text-wb-text-tertiary min-w-0 border-0 p-0 m-0"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={selectProps?.placeholder as string | undefined}
      autoComplete="off"
    />
  );
};

const createValueContainer = <T, IsMulti extends boolean>(
  renderTitle?: (data: T | null) => ReactNode,
  renderValue?: (
    data: T,
    props: ValueContainerProps<SelectorOption<T>, IsMulti>
  ) => ReactNode,
  valueClasses?: string
) => {
  const CustomValueContainer = (
    props: ValueContainerProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    if (renderTitle) {
      const selectedOpt = props.getValue()[0];
      // Use value as fallback if data is not provided
      const data = selectedOpt
        ? ((selectedOpt.data ?? selectedOpt.value) as T)
        : null;

      // When closed, show the title
      if (!props.selectProps.menuIsOpen) {
        return (
          <div
            className={twMerge(
              FLEX_CONTAINER,
              FLEX_COL,
              'justify-center',
              CARD_PADDING
            )}>
            {renderTitle(data)}
          </div>
        );
      }

      return (
        <div className={twMerge(FLEX_ROW_CENTER, FLEX_CONTAINER)}>
          <div
            className={twMerge(
              FIXED_SIZE,
              'text-wb-text-tertiary flex items-center justify-center pr-8'
            )}>
            <Icon icon="search" size="sm" />
          </div>
          <components.ValueContainer {...props} className={FLEX_CONTAINER} />
        </div>
      );
    }

    // Standard mode: Show selected value with custom rendering
    if (renderValue && !props.isMulti) {
      const selectedOpt = props.getValue()[0];
      if (!selectedOpt) {
        return <components.ValueContainer {...props} />;
      }
      // Use value as fallback if data is not provided
      const data = (selectedOpt.data ?? selectedOpt.value) as T;
      return (
        <components.ValueContainer {...props} className={valueClasses}>
          {renderValue(data, props)}
        </components.ValueContainer>
      );
    }

    return <components.ValueContainer {...props} className={valueClasses} />;
  };
  return CustomValueContainer;
};

const createControlComponent = <T, IsMulti extends boolean>(
  renderTitle?: (data: T | null) => ReactNode,
  menuPlacement?: 'top' | 'bottom'
) => {
  const ControlComponent = (
    props: ControlProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ): JSX.Element => {
    if (renderTitle) {
      const baseClasses = twMerge(
        'w-full h-[50px]',
        FLEX_COL,
        'border',
        'relative justify-center'
      );

      if (!props.menuIsOpen) {
        const closedClasses = twMerge(
          baseClasses,
          'group min-w-[140px] cursor-pointer',
          'border-transparent',
          'rounded-md',
          CARD_BG_CLOSED,
          'transition-all hover:shadow-sm'
        );

        return (
          <div
            className={closedClasses}
            onClick={() => props.selectProps.onMenuOpen?.()}>
            <div className={twMerge('w-full', FLEX_ROW_CENTER, 'min-w-0')}>
              {props.children}
            </div>
          </div>
        );
      }

      const isTop = menuPlacement === 'top';

      const openBaseClasses = twMerge(
        FLEX_CONTAINER,
        FLEX_COL,
        'items-start justify-center',
        CARD_BG_OPEN
      );

      const roundedClasses = `rounded-${isTop ? 'b' : 't'}-md`;
      const borderClasses = isTop ? 'border-t-0' : 'border-b-0';

      const openClasses = twMerge(
        baseClasses,
        'border-wb-border',
        borderClasses,
        openBaseClasses,
        roundedClasses,
        'shadow-none'
      );

      const controlBoxShadow = getOpenControlBoxShadow(
        menuPlacement === 'top' ? 'top' : 'bottom'
      );

      return (
        <div className={openClasses} style={{boxShadow: controlBoxShadow}}>
          <div
            className={twMerge(
              'px-6 py-4 w-full flex items-center hover:outline-none',
              CARD_BG_OPEN
            )}>
            {/*
             * Render the Control content without react-select's default innerProps.onMouseDown
             * (onControlMouseDown). That default handler calls onMenuClose() whenever the menu
             * is open, which would close the menu on any click — including clicking the search
             * input to type. We keep innerRef so react-select can track the control element,
             * and we keep all other innerProps (aria attrs, etc.) but explicitly drop onMouseDown.
             */}
            <div
              // oxlint-disable-next-line react/refs
              ref={props.innerRef}
              // oxlint-disable-next-line react/refs
              {...props.innerProps}
              onMouseDown={undefined}
              className={twMerge(
                'w-full flex flex-row cursor-pointer',
                CARD_BG_OPEN,
                'border-0 shadow-none outline-none p-0 min-h-0'
              )}>
              {/* oxlint-disable-next-line react/refs */}
              {props.children}
            </div>
          </div>
        </div>
      );
    }

    return (
      <components.Control
        // oxlint-disable-next-line react/refs
        {...props}
        // oxlint-disable-next-line react/refs
        innerProps={{
          // oxlint-disable-next-line react/refs
          ...props.innerProps,
          onMouseDown: e => {
            if (!props.menuIsOpen) {
              props.selectProps.onMenuOpen?.();
            } else {
              props.selectProps.onMenuClose?.();
            }
            e.preventDefault();
          },
        }}
      />
    );
  };
  return ControlComponent;
};

// In card mode the native <input> placeholder attribute (set via selectProps in
// CardSearchInput) handles the hint text, so the react-select Placeholder should
// render nothing. A module-level constant keeps the reference stable.
const NullPlaceholder = () => null;

const createMenuComponent = <T, IsMulti extends boolean>(
  renderTitle?: (data: T | null) => ReactNode,
  onPlacementChange?: (placement: 'top' | 'bottom') => void
) => {
  const MenuComponent = (
    props: MenuProps<SelectorOption<T>, IsMulti, GroupBase<SelectorOption<T>>>
  ) => {
    // Notify parent of placement
    React.useEffect(() => {
      if (onPlacementChange && props.placement) {
        onPlacementChange(props.placement);
      }
    }, [props.placement]);

    if (renderTitle) {
      const isTop = props.placement === 'top';

      const menuBaseClasses = twMerge(
        ABSOLUTE_OVERLAY,
        FLEX_COL,
        'w-full bg-wb-surface border-x border-wb-border'
      );

      const menuRoundedClasses = `rounded-none rounded-${isTop ? 't' : 'b'}-md`;
      const menuPlacementClasses = isTop
        ? 'bottom-full mb-0 border-t border-b shadow-none '
        : 'top-full mt-0 border-t border-b shadow-none';

      const menuClasses = twMerge(
        menuBaseClasses,
        menuPlacementClasses,
        menuRoundedClasses
      );

      const menuBoxShadow = getOpenMenuBoxShadow(
        props.placement === 'top' ? 'top' : 'bottom'
      );

      const {innerProps, ...restMenuProps} = props;
      return (
        <components.Menu
          {...restMenuProps}
          className={menuClasses}
          innerProps={{
            ...innerProps,
            style: {
              ...(innerProps?.style as React.CSSProperties),
              boxShadow: menuBoxShadow,
            },
          }}>
          {props.children}
        </components.Menu>
      );
    }
    // Standard mode: default menu with full width
    return <components.Menu {...props} className="w-full" />;
  };
  return MenuComponent;
};

const createClearIndicator = (
  renderTitle: ((data: any) => ReactNode) | undefined
) => {
  const ClearIndicator = <T, IsMulti extends boolean>(
    props: ClearIndicatorProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    if (renderTitle) {
      if (!props.selectProps.menuIsOpen) {
        return null;
      }

      return (
        <components.ClearIndicator
          {...props}
          innerProps={{
            ...props.innerProps,
            onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.stopPropagation();
              props.clearValue();
              // Close menu after clearing
              props.selectProps.onMenuClose?.();
            },
          }}
          className="absolute inset-0 invisible group-hover/control:visible cursor-pointer text-[light-dark(var(--moon-600),var(--moon-400))] flex items-center justify-center">
          <Icon icon="close" size="sm" />
        </components.ClearIndicator>
      );
    }

    // Standard mode: default clear indicator
    return <components.ClearIndicator {...props} />;
  };
  return ClearIndicator;
};

const MenuListComponent = <T, IsMulti extends boolean>(
  props: MenuListProps<SelectorOption<T>, IsMulti, GroupBase<SelectorOption<T>>>
) => (
  <components.MenuList
    {...props}
    className="mx-0 w-full flex-1 flex flex-col gap-2"
  />
);

const createDropdownIndicator = <T, IsMulti extends boolean>(
  renderTitle: ((data: T | null) => ReactNode) | undefined
) => {
  const DropdownIndicator = (
    props: DropdownIndicatorProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    const menuIsOpen = props.selectProps.menuIsOpen;

    // Always render to reserve space, overlay with clear indicator when open
    if (renderTitle) {
      return (
        <div
          className={twMerge(
            'pr-8 flex items-center justify-center text-wb-text-tertiary transition-opacity',
            menuIsOpen
              ? 'invisible pointer-events-none'
              : 'invisible group-hover:visible'
          )}>
          <Icon icon="chevron-down" size="sm" />
        </div>
      );
    }

    return (
      <div className="pr-8 flex items-center text-wb-text-tertiary">
        <Icon icon={menuIsOpen ? 'chevron-up' : 'chevron-down'} size="sm" />
      </div>
    );
  };
  return DropdownIndicator;
};

const createIndicatorContainerComponent = <T, IsMulti extends boolean>(
  renderTitle: ((data: T | null) => ReactNode) | undefined
) => {
  const IndicatorContainerComponent = (
    props: IndicatorsContainerProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    if (renderTitle) {
      return (
        <components.IndicatorsContainer
          {...props}
          className="h-full relative"
        />
      );
    }
    return <components.IndicatorsContainer {...props} className="h-full" />;
  };
  return IndicatorContainerComponent;
};

const createContainerComponent = <T, IsMulti extends boolean>(
  renderTitle: ((data: T | null) => ReactNode) | undefined
) => {
  const ContainerComponent = (
    props: ContainerProps<
      SelectorOption<T>,
      IsMulti,
      GroupBase<SelectorOption<T>>
    >
  ) => {
    if (renderTitle) {
      return (
        <div className="relative w-full h-[50px]">
          <components.SelectContainer
            {...props}
            className="flex flex-row w-full h-full"
          />
        </div>
      );
    }

    return (
      <components.SelectContainer
        {...props}
        className={`flex flex-row h-full min-w-[200px] items-center justify-start`}
      />
    );
  };
  return ContainerComponent;
};

// ============================================================================
// Main Component
// ============================================================================

function GenericSelectorDropdownInner<T, IsMulti extends boolean = false>(
  {
    size,
    selectedValue,
    onSelect,
    onSelectMulti,
    options,
    isMulti,
    isSearchable = true,
    isClearable = true,
    menuIsOpen,
    onMenuOpen,
    onMenuClose,
    placeholder = 'Select...',
    renderOption,
    renderTitle,
    renderValue,
    menuItemClasses,
    valueClasses,
    className,
    onInputChange,
    filterOption,
    isLoading,
    isOptionDisabled,
  }: GenericSelectorDropdownProps<T, IsMulti>,
  ref: React.Ref<SelectInstance<SelectorOption<T>, IsMulti>>
) {
  const [isMenuOpen, setIsMenuOpen] = useState(menuIsOpen ?? false);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  // This prevents two menus from being open simultaneously.
  const containerRef = useRef<HTMLDivElement>(null);
  useClickAway(containerRef, () => {
    if (isMenuOpen) {
      handleMenuClose();
    }
  });

  const [menuPlacement, setMenuPlacement] = React.useState<'top' | 'bottom'>(
    'bottom'
  );

  const handleChange = useCallback(
    (option: OnChangeValue<SelectorOption<T>, IsMulti>) => {
      if (isMulti) {
        // Multi-select mode
        const multiOption = option as MultiValue<SelectorOption<T>>;
        if (onSelectMulti) {
          onSelectMulti(multiOption.map(opt => (opt.data ?? opt.value) as T));
        }
      } else {
        // Single-select mode
        const singleOption = option as SingleValue<SelectorOption<T>>;
        if (onSelect) {
          onSelect(
            singleOption
              ? ((singleOption.data ?? singleOption.value) as T)
              : null
          );
        }
        // Close the menu after selection in single mode (card mode only)
        if (renderTitle) {
          handleMenuClose();
        }
      }
    },
    [isMulti, onSelectMulti, onSelect, renderTitle, handleMenuClose]
  );

  // Create component instances with current props
  const CustomOption = useMemo(
    () => createCustomOption<T, IsMulti>(renderOption, menuItemClasses),
    [renderOption, menuItemClasses]
  );
  const CustomSingleValue = useMemo(() => createSingleValue<T, IsMulti>(), []);
  const CustomMultiValue = useMemo(() => createMultiValue<T, IsMulti>(), []);
  const CustomValueContainer = useMemo(
    () =>
      createValueContainer<T, IsMulti>(renderTitle, renderValue, valueClasses),
    [renderTitle, renderValue, valueClasses]
  );
  const ControlComponent = useMemo(
    () => createControlComponent<T, IsMulti>(renderTitle, menuPlacement),
    [renderTitle, menuPlacement]
  );
  const MenuComponent = useMemo(
    () => createMenuComponent<T, IsMulti>(renderTitle, setMenuPlacement),
    [renderTitle]
  );
  const ClearIndicator = useMemo(
    () => createClearIndicator(renderTitle),
    [renderTitle]
  );
  const DropdownIndicator = useMemo(
    () => createDropdownIndicator<T, IsMulti>(renderTitle),
    [renderTitle]
  );
  const IndicatorContainerComponent = useMemo(
    () => createIndicatorContainerComponent<T, IsMulti>(renderTitle),
    [renderTitle]
  );
  const ContainerComponent = useMemo(
    () => createContainerComponent<T, IsMulti>(renderTitle),
    [renderTitle]
  );

  // When menu is open, elevate the entire control above siblings so dropdown overlays adjacent controls
  const containerClassName = twMerge(
    renderTitle ? 'relative w-full h-full group/control' : '',
    className
  );
  const selectClassName = twMerge(
    renderTitle
      ? isMenuOpen
        ? 'w-full h-full text-[light-dark(var(--moon-600),var(--moon-400))]'
        : 'w-full h-full group'
      : 'text-[light-dark(var(--moon-600),var(--moon-400))]',
    className
  );
  const shouldRenderValue = renderTitle ? !isMenuOpen : true;

  // Forward only what the user types. react-select also calls onInputChange
  // with '' when the input blurs, the menu closes, or an option is picked, and
  // those resets should not reach whatever state the caller keeps (a search, or
  // config written through updateConfig).
  const handleInputChange = useCallback(
    (value: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        onInputChange?.(value);
      }
    },
    [onInputChange]
  );

  return (
    <div className={containerClassName} ref={containerRef}>
      <Select<SelectorOption<T>, IsMulti>
        className={selectClassName}
        value={selectedValue as PropsValue<SelectorOption<T>>}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        size={size}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isMulti={isMulti}
        hideSelectedOptions={false}
        menuPlacement="auto"
        menuIsOpen={isMenuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        controlShouldRenderValue={shouldRenderValue}
        onInputChange={onInputChange != null ? handleInputChange : undefined}
        filterOption={filterOption ?? undefined}
        isLoading={isLoading}
        isOptionDisabled={isOptionDisabled}
        components={{
          SelectContainer: ContainerComponent,
          Control: ControlComponent,
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          MultiValue: CustomMultiValue,
          ValueContainer: CustomValueContainer,
          Menu: MenuComponent,
          MenuList: MenuListComponent,
          IndicatorsContainer: IndicatorContainerComponent,
          ClearIndicator: ClearIndicator,
          DropdownIndicator: DropdownIndicator,
          // Card mode: replace react-select's auto-sizing Input (which starts at ~2px
          // wide when empty) with a full-width native input so the search area is
          // always directly clickable without relying on an absolute-positioned overlay.
          ...(renderTitle
            ? {Input: CardSearchInput, Placeholder: NullPlaceholder}
            : {}),
        }}
      />
    </div>
  );
}

export const GenericSelectorDropdown = forwardRef(
  GenericSelectorDropdownInner
) as <T, IsMulti extends boolean = false>(
  props: GenericSelectorDropdownProps<T, IsMulti> & {
    ref?: React.Ref<SelectInstance<SelectorOption<T>, IsMulti>>;
  }
) => ReturnType<typeof GenericSelectorDropdownInner>;
